import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  type WorkflowStepCode,
  FilterOperator,
  useSearchOwnedAndAssignedTasksQuery,
  WorkflowCode,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import {
  type GridEventListener,
  type GridRowParams,
  DataGrid,
  dataGridSortDirection,
  ErrorBox,
  NoRowsOverlay,
} from '@shared/components';

import { useQuestionCreationTaskListSearchParams } from '../../hooks';

import { columns, type QuestionCreationTask } from './columns';

export default function QuestionCreationTaskList() {
  const navigate = useNavigate();
  const [
    { code, page, pageSize, sort },
    { changePageNumber, changePageSize, changeSortDirection, getStatusFiltersByCode },
  ] = useQuestionCreationTaskListSearchParams();

  const {
    data: searchOwnedAndAssignedTasksData,
    loading: isSearchingOwnedAndAssignedTasks,
    error: searchOwnedAndAssignedTasksError,
  } = useSearchOwnedAndAssignedTasksQuery({
    variables: {
      searchOwnedAndAssignedTasksInput: {
        // NOTE: as unknown as WorkflowStepCode[] is a workaround for enum incompatibility
        // TODO: remove as unknown as when the enum incompatibility is fixed
        currentWorkflowStepCode: getStatusFiltersByCode(code) as unknown as WorkflowStepCode[],
        workflowCode: WorkflowCode.QuestionCreation,
        filter: {
          conditions: [
            {
              field: 'parentId',
              operator: FilterOperator.IsNull,
            },
          ],
        },
        order: {
          field: 'updatedAt',
          type: ORDER_TYPE[sort],
        },
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const ownedAndAssignedTasks =
    searchOwnedAndAssignedTasksData?.wflSearchOwnedAndAssignedTasks.tasks ?? [];
  const ownedAndAssignedTasksCount =
    searchOwnedAndAssignedTasksData?.wflSearchOwnedAndAssignedTasks.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<QuestionCreationTask>,
  ) => {
    navigate('/task-management/question-creation/:taskId', {
      params: {
        taskId: params.row.id,
      },
    });
  };

  if (searchOwnedAndAssignedTasksError) {
    return <ErrorBox />;
  }

  return (
    <DataGrid
      columns={columns}
      getRowId={(row) => row.id}
      rows={ownedAndAssignedTasks}
      loading={isSearchingOwnedAndAssignedTasks}
      rowCount={ownedAndAssignedTasksCount}
      disableColumnFilter
      disableColumnMenu
      hideFooter={isSearchingOwnedAndAssignedTasks || ownedAndAssignedTasksCount === 0}
      rowHeight={48}
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      slotProps={{
        footer: {
          total: ownedAndAssignedTasksCount,
          pagination: {
            page: page,
            count: ownedAndAssignedTasksCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const { sort } = sorting[0] ?? {
          sort: dataGridSortDirection[0],
        };
        if (sort) {
          changeSortDirection(sort);
        }
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: 'updatedAt', sort }],
        },
      }}
      sx={(theme) => ({
        '.MuiDataGrid-cell': {
          padding: theme.spacing(3),
          fontSize: theme.typography.body2.fontSize,
        },
        '.MuiDataGrid-columnHeader': {
          padding: theme.spacing(3),
          borderRadius: theme.shape.rounded(0),
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderColor: theme.palette.grey[200],
        },
        borderRadius: theme.shape.rounded(0),
        '.MuiDataGrid-columnHeaders': {
          borderRadius: theme.shape.rounded(0),
        },
        backgroundColor: theme.palette.background.paper,
        '--DataGrid-overlayHeight': '100%',
        '.MuiDataGrid-overlayWrapper': {
          height: '100%',
        },
      })}
    />
  );
}
